import { navigate } from 'svelte-routing';

const API_URL = '/api';

// Helper to make authenticated API requests
export async function authFetch(endpoint, options = {}, returnRawResponse = false) {
  const token = localStorage.getItem('token');
  
  if (!token) {
    navigate('/login');
    throw new Error('No authentication token found');
  }
  
  // Don't add Content-Type: application/json if we're sending FormData
  const isFormData = options.body instanceof FormData;
  
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  
  try {
    // Try with API_URL prefix first
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers
      });
      
      // If unauthorized, redirect to login
      if (response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
      }
      
      // Return the raw response if requested (for blob, etc.)
      if (returnRawResponse) {
        if (!response.ok) {
          // Try to parse error response as JSON
          try {
            const errorData = await response.clone().json();
            throw new Error(errorData.message || 'Terjadi kesalahan pada server');
          } catch (jsonError) {
            throw new Error('Terjadi kesalahan pada server');
          }
        }
        return response;
      }
      
      // Parse JSON for regular responses
      const data = await response.json();
      
      // Handle API errors
      if (!response.ok) {
        throw new Error(data.message || 'Terjadi kesalahan pada server');
      }
      
      return data;
    } catch (firstError) {
      // Try without API_URL prefix
      console.log('Trying alternative API endpoint for request...');
      const response = await fetch(`${endpoint}`, {
        ...options,
        headers
      });
      
      // If unauthorized, redirect to login
      if (response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
      }
      
      // Return the raw response if requested (for blob, etc.)
      if (returnRawResponse) {
        if (!response.ok) {
          // Try to parse error response as JSON
          try {
            const errorData = await response.clone().json();
            throw new Error(errorData.message || 'Terjadi kesalahan pada server');
          } catch (jsonError) {
            throw new Error('Terjadi kesalahan pada server');
          }
        }
        return response;
      }
      
      // Parse JSON for regular responses
      const data = await response.json();
      
      // Handle API errors
      if (!response.ok) {
        throw new Error(data.message || 'Terjadi kesalahan pada server');
      }
      
      return data;
    }
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
}

// Auth API
export async function login(username, password) {
  // Try both API endpoints paths
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Login gagal');
    }
    
    // Save token to localStorage
    localStorage.setItem('token', data.token);
    
    return data;
  } catch (error) {
    console.log('Trying alternative API endpoint...');
    // Try alternative endpoint
    const response = await fetch(`/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Login gagal');
    }
    
    // Save token to localStorage
    localStorage.setItem('token', data.token);
    
    return data;
  }
}

export async function checkAuth() {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return null;
  }
  
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Authentication failed');
    }
    
    return await response.json();
  } catch (error) {
    // Try alternative endpoint
    try {
      console.log('Trying alternative API endpoint for auth check...');
      const response = await fetch(`/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Authentication failed');
      }
      
      return await response.json();
    } catch (secondError) {
      console.error('Auth check failed:', secondError);
      localStorage.removeItem('token');
      return null;
    }
  }
}

export function logout() {
  localStorage.removeItem('token');
  navigate('/login');
}

// User API
export async function getUsers() {
  return authFetch('/users');
}

export async function createUser(userData) {
  return authFetch('/users', {
    method: 'POST',
    body: JSON.stringify(userData)
  });
}

export async function updateUser(userId, userData) {
  return authFetch(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData)
  });
}

export async function deleteUser(userId) {
  return authFetch(`/users/${userId}`, {
    method: 'DELETE'
  });
}

// Voucher Title API
export async function getVoucherTitles() {
  return authFetch('/vouchers/titles');
}

export async function createVoucherTitle(titleData) {
  return authFetch('/vouchers/titles', {
    method: 'POST',
    body: JSON.stringify(titleData)
  });
}

export async function updateVoucherTitle(titleId, titleData) {
  return authFetch(`/vouchers/titles/${titleId}`, {
    method: 'PUT',
    body: JSON.stringify(titleData)
  });
}

export async function deleteVoucherTitle(titleId) {
  return authFetch(`/vouchers/titles/${titleId}`, {
    method: 'DELETE'
  });
}

// Voucher Category API
export async function getVoucherCategories(titleId) {
  return authFetch(`/vouchers/categories/${titleId}`);
}

export async function createVoucherCategory(categoryData) {
  // Using FormData for file upload
  const formData = new FormData();
  
  Object.keys(categoryData).forEach(key => {
    if (key === 'image') {
      formData.append('image', categoryData.image);
    } else {
      formData.append(key, categoryData[key]);
    }
  });
  
  const token = localStorage.getItem('token');
  
  if (!token) {
    navigate('/login');
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(`${API_URL}/vouchers/categories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      navigate('/login');
      throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Terjadi kesalahan pada server');
    }
    
    return data;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
}

export async function updateVoucherCategory(categoryId, categoryData) {
  // Using FormData for file upload if image exists
  const formData = new FormData();
  
  Object.keys(categoryData).forEach(key => {
    if (key === 'image' && categoryData.image) {
      formData.append('image', categoryData.image);
    } else if (key !== 'image') {
      formData.append(key, categoryData[key]);
    }
  });
  
  const token = localStorage.getItem('token');
  
  if (!token) {
    navigate('/login');
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(`${API_URL}/vouchers/categories/${categoryId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      navigate('/login');
      throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Terjadi kesalahan pada server');
    }
    
    return data;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
}

export async function deleteVoucherCategory(categoryId) {
  return authFetch(`/vouchers/categories/${categoryId}`, {
    method: 'DELETE'
  });
}

// Voucher API
export async function getVouchers(categoryId, page = 1, limit = 50, status = '') {
  return authFetch(`/vouchers/${categoryId}?page=${page}&limit=${limit}&status=${status}`);
}

export async function markVoucherAsUsed(code) {
  return authFetch(`/vouchers/mark-used/${code}`, {
    method: 'POST'
  });
}

export async function markVoucherAsUnused(code) {
  return authFetch(`/vouchers/mark-unused/${code}`, {
    method: 'POST'
  });
}

// Export API
export async function exportVouchersToPDF(categoryId, start = 0, count = 100) {
  const token = localStorage.getItem('token');
  
  if (!token) {
    navigate('/login');
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(`${API_URL}/export/vouchers/${categoryId}?start=${start}&count=${count}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      navigate('/login');
      throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Terjadi kesalahan pada server');
    }
    
    // Get the blob of the PDF
    const blob = await response.blob();
    
    // Create a download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `vouchers-${categoryId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    
    return true;
  } catch (error) {
    console.error('Export PDF Error:', error);
    throw error;
  }
}

// Analytics API
export async function getVoucherAnalytics(titleId = null, categoryId = null) {
  const params = new URLSearchParams();
  if (titleId) params.append('titleId', titleId);
  if (categoryId) params.append('categoryId', categoryId);
  
  const queryString = params.toString();
  const url = queryString ? `/vouchers/analytics/data?${queryString}` : '/vouchers/analytics/data';
  
  return authFetch(url);
}

// Download voucher with QR code (admin only)
export async function downloadVoucherWithQR(code) {
  try {
    // Show loading indicator
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    // Using fetch directly for blob handling
    const response = await fetch(`${API_URL}/vouchers/download/${code}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf, image/png, */*',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
      }
      
      // Try to parse error response as JSON
      try {
        const errorData = await response.clone().json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      } catch (jsonError) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    }
    
    // Convert the response to a blob
    const blob = await response.blob();
    
    if (blob.size === 0) {
      throw new Error('Voucher tidak dapat dihasilkan. File kosong.');
    }
    
    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    // Set up download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `voucher-${code}.pdf`;
    
    // Append to document body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Revoke the blob URL to free memory
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Error downloading voucher:', error);
    throw error;
  }
}
